import * as React from 'react';
import { Form, Icon as LegacyIcon } from '@ant-design/compatible';
import '@ant-design/compatible/assets/index.css';
import { Modal, Select, Input, Alert, DatePicker, InputNumber, message, Row } from 'antd';
import { FormComponentProps } from '@ant-design/compatible/lib/form/Form';
import { connect } from 'react-redux';
import moment from 'moment';
import { imageUploadValidation, normFile } from '../../utils/ImageUtils';
import { getProtectedImageUriForSupplier } from '../../utils/FileAccessUtils';
import { getBackendUri } from '../../utils/EnvConfigUtils';
import TextAreaWithAudio from '../audio_note_recorder/TextAreaWithAudio';
import {
	compareStrings,
	getDisabledDate,
	getDisabledDateTime,
	getImageCountErrorRules,
	getObjectValues,
	getServiceCallConfigValueBasedOnSupplierOrInternalTeam,
	nullSafeGet,
	nullSafeGetOrElse,
} from '../../utils/DataAccessUtils';
import { WORK_ORDER_STATUSES } from '../../constants/WorkOrderStatuses';
import { retrieveCachedUserDetails } from '../../thunks/session_thunks';
import { resolutionTypesRestCrudThunksForSupplier } from '../../thunks/resolution_types_thunks';
import OWAsyncSelect from '../ow_async_select/OWAsyncSelect';
import OWUpload from '../OWUpload';
import { taskItemsRestCrudThunksForSupplier } from '../../thunks/task_items_thunks';
import { isWOTaskCheckFailed } from '../../utils/WorkOrderUtils';
import { wallTimeToUtcIso } from '../../utils/DataFormatterUtils';
import { DateTimeWithTimeZone } from '../date_time_with_time_zone/DateTimeWithTimeZone';

const FormItem = Form.Item;
export const DATE_FORMAT = 'MMMM D, YYYY h:mm a';

interface ManualCheckOutModalFormProps extends FormComponentProps {
	onSubmit: any;
	onCancel: any;
	currentUser: any;
	companyConfig: any;
	createErrors: any;
	updateErrors: any;
	visible: boolean;
	creating: boolean;
	updating: boolean;

	formData: any;
	privateSupplier: boolean;
	resolutionTypes: any;
	fetchResolutionTypes: any;
	fetchMultipleResolutionTypes: any;
	workOrder: any;
	fetchTaskItems: any;
}

const CONTRACTOR_CHECK_OUT_OPTIONS = [
	{
		displayName: 'Work Complete',
		value: WORK_ORDER_STATUSES.waitingForReview,
	},
	{
		displayName: 'Quote Required',
		value: WORK_ORDER_STATUSES.waitingForQuote,
	},
	{
		displayName: 'Delayed by Third Party',
		value: WORK_ORDER_STATUSES.delayedByThirdParty,
	},
	{
		displayName: 'Work Incomplete With Reason',
		value: WORK_ORDER_STATUSES.workIncompleteWithReason,
	},
];

const INTERNAL_TECH_CHECK_OUT_OPTIONS = [
	{
		displayName: 'Work Complete',
		value: WORK_ORDER_STATUSES.internalTechWorkComplete,
	},
	{
		displayName: 'Delayed by Third Party',
		value: WORK_ORDER_STATUSES.delayedByThirdParty,
	},
	{
		displayName: 'Work Incomplete With Reason',
		value: WORK_ORDER_STATUSES.workIncompleteWithReason,
	},
];

const COMPLETED_CHECKOUT_VALUES = [
	WORK_ORDER_STATUSES.waitingForReview,
	WORK_ORDER_STATUSES.internalTechWorkComplete,
];

const PARTS_REQUESTED_CHECK_OUT_OPTION = [
	{
		displayName: 'Parts Requested',
		value: WORK_ORDER_STATUSES.partsRequested,
	},
];

const PARTS_ON_ORDER_CHECK_OUT_OPTION = [
	{
		displayName: 'Parts on Order',
		value: WORK_ORDER_STATUSES.partsOnOrder,
	},
];

class ManualCheckOutModalForm extends React.Component<ManualCheckOutModalFormProps, any> {
	constructor(props) {
		super(props);
		this.state = {
			previewVisible: false,
			uploadLoading: false,
			previewImage: false,
			photoInfo: [],
			allPhotos: [],
			taskItems: [],
			isAudioBusy: false,
		};
	}

	componentDidMount() {
		const { fetchTaskItems, workOrder, form } = this.props;

		if (!nullSafeGet('id', workOrder)) return;
		this.setState({ taskItemsFetching: true });
		fetchTaskItems({ workOrderId: workOrder.id }, 'checkoutModalFormIndex', {
			current: 1,
			pageSize: 500,
		})
			.then((taskItems) =>
				this.setState(
					{
						taskItems,
						taskItemsFetching: false,
					},
					() => {
						form.validateFields(['checkOutStatus'], { force: true });
					}
				)
			)
			.catch(message.error)
			.finally(() => this.setState({ taskItemsFetching: false }));
	}

	handlePreview = (obj) => {
		let thumbUrl = null;
		let fullUrl = null;
		let photoInfo = null;
		if (obj.response.data) {
			const imgFileId = obj.response.data.fileId;
			const imgFileName = obj.response.data.fileName;
			thumbUrl = getProtectedImageUriForSupplier(imgFileId, imgFileName, 100, 100);
			fullUrl = getProtectedImageUriForSupplier(imgFileId, imgFileName, 400, 400);
			photoInfo = {
				uid: imgFileId,
				size: 1,
				name: imgFileName,
				filename: imgFileName,
				url: fullUrl,
				status: 'done',
				thumbUrl: thumbUrl,
				response: { data: { fileName: imgFileName, fileId: imgFileId } },
				type: 'image/jpeg',
			};
		}

		this.setState({
			photoInfo: [photoInfo],
			previewImage: fullUrl || thumbUrl,
			previewVisible: true,
		});
	};

	handleImageUpdate = (info) => {
		if (info.file.status === 'done') {
			this.setState({ allPhotos: info.fileList });
		} else if (info.file.status === undefined) {
			info.fileList = this.state.allPhotos;
		}
	};

	handleRemove = (file) => {
		const allPhotos = nullSafeGetOrElse('state.allPhotos', this, []);
		this.setState({
			allPhotos: allPhotos.filter((_) => _.uid !== file.uid),
		});
	};

	handleCancel = () => this.setState({ previewVisible: false });

	render() {
		const {
			visible,
			currentUser,
			onCancel,
			onSubmit,
			form,
			formData,
			createErrors,
			updateErrors,
			privateSupplier,
			companyConfig,
			creating,
			updating,
			resolutionTypes,
			fetchResolutionTypes,
			fetchMultipleResolutionTypes,
			workOrder,
		} = this.props;
		const { previewVisible, previewImage } = this.state;
		const { getFieldDecorator } = form;
		const okText = 'Check Out';
		const titleText = 'Remote Check Out';
		const resolutionsFetching = nullSafeGet('fetching', resolutionTypes);
		const hasResolutionTypes = resolutionsFetching
			? true
			: getObjectValues(nullSafeGetOrElse('records', resolutionTypes, {})).length > 0;
		const photoStrings = this.state.allPhotos.map((p) => {
			const fileName = nullSafeGetOrElse('response.data.fileName', p, '');
			const fileId = nullSafeGetOrElse('response.data.fileId', p, '');
			return `${fileId}/${fileName}`;
		});

		const BACKEND_URI = getBackendUri();
		const uploadHeaders = { 'X-Auth-Token': retrieveCachedUserDetails(['token']).token };
		getFieldDecorator(`id`, { initialValue: formData.id });
		getFieldDecorator(`checkOutByEmail`, { initialValue: currentUser.email });
		getFieldDecorator(`checkOutImages`, { initialValue: photoStrings });
		const userType = nullSafeGet('userType', currentUser);

		const checkOutNotesMandatory = getServiceCallConfigValueBasedOnSupplierOrInternalTeam(
			workOrder,
			currentUser,
			'checkOutNotesMandatory',
			false
		);
		const numImagesRequired = getServiceCallConfigValueBasedOnSupplierOrInternalTeam(
			workOrder,
			currentUser,
			'numImagesRequiredOnCheckOut',
			0
		);
		const imageCountErrorMessage = `You need to upload at least ${numImagesRequired} photo${
			numImagesRequired > 1 ? 's' : ''
		}`;

		const ADDITIONAL_CHECK_OUT_OPTIONS = nullSafeGetOrElse(
			'detail.config.partsOrderingConfig.needPartsRequisitionBeforeOrdering',
			companyConfig,
			false
		)
			? PARTS_REQUESTED_CHECK_OUT_OPTION
			: PARTS_ON_ORDER_CHECK_OUT_OPTION;

		const partsRequestedStatus = compareStrings(
			workOrder.status,
			WORK_ORDER_STATUSES.partsRequested
		);

		const checkOutOptions = privateSupplier ? (
			<Select style={{ maxWidth: 300 }}>
				{[
					...(partsRequestedStatus ? [] : INTERNAL_TECH_CHECK_OUT_OPTIONS),
					...ADDITIONAL_CHECK_OUT_OPTIONS,
				].map((d) => (
					<Select.Option key={d.value} value={d.value}>
						{d.displayName}
					</Select.Option>
				))}
			</Select>
		) : (
			<Select style={{ maxWidth: 300 }}>
				{[
					...(partsRequestedStatus ? [] : CONTRACTOR_CHECK_OUT_OPTIONS),
					...ADDITIONAL_CHECK_OUT_OPTIONS,
				].map((d) => (
					<Select.Option key={d.value} value={d.value}>
						{d.displayName}
					</Select.Option>
				))}
			</Select>
		);

		const tasksCheckFailed =
			this.state.taskItemsFetching || isWOTaskCheckFailed(workOrder, this.state.taskItems);
		const { checkInTime } = formData;
		const checkOutTimeZone =
			form.getFieldValue('checkOutTimeZone') || nullSafeGet('location.timeZoneId', workOrder);

		return (
			<Modal
				visible={visible}
				width={600}
				title={titleText}
				okText={okText}
				onCancel={onCancel}
				onOk={onSubmit}
				closable={false}
				confirmLoading={creating || updating}
				okButtonProps={{
					disabled: this.state.isAudioBusy,
				}}
			>
				<Form layout="vertical" className="supplierForm">
					{createErrors.length > 0 ? (
						<FormItem>
							<Alert message={createErrors.join(' ')} type="error" />
						</FormItem>
					) : null}
					{updateErrors.length > 0 ? (
						<FormItem>
							<Alert message={updateErrors.join(' ')} type="error" />
						</FormItem>
					) : null}
					<FormItem label="Check Out Status">
						{getFieldDecorator('checkOutStatus', {
							rules: [
								{
									required: true,
									message: 'Please select a status',
								},
								{
									message: 'All tasks need to be completed before checkout!',
									validator: function (_, value) {
										if (tasksCheckFailed && COMPLETED_CHECKOUT_VALUES.includes(value)) {
											return Promise.reject(
												new Error('All tasks need to be completed before checkout!')
											);
										}
										return Promise.resolve();
									}.bind(this),
								},
							],
							initialValue: partsRequestedStatus
								? ADDITIONAL_CHECK_OUT_OPTIONS[0].value
								: formData.checkOutStatus || privateSupplier
								? INTERNAL_TECH_CHECK_OUT_OPTIONS[0].value
								: CONTRACTOR_CHECK_OUT_OPTIONS[0].value,
						})(checkOutOptions)}
					</FormItem>
					{(!COMPLETED_CHECKOUT_VALUES.includes(
						form.getFieldsValue(['checkOutStatus']).checkOutStatus
					) && (
						<FormItem label="Estimated completion date ">
							{getFieldDecorator(`estimatedCompletionDate`, {
								initialValue: formData.estimatedCompletionDate
									? moment(formData.estimatedCompletionDate)
									: undefined,
							})(<DatePicker style={{ width: '100%' }} showTime={false} format={'MMMM D, YYYY'} />)}
						</FormItem>
					)) ||
						null}
					<FormItem label="Number of Techs">
						{getFieldDecorator('numberOfTechs', {
							rules: [
								{
									required: true,
									message: 'Please enter the number of techs involved',
								},
							],
							initialValue: 1 + nullSafeGetOrElse('additionalTechnicianEmails.length', formData, 0),
						})(<InputNumber style={{ maxWidth: 100 }} placeholder="techs" />)}
					</FormItem>
					<DateTimeWithTimeZone
						form={form}
						location={nullSafeGet('location', workOrder)}
						displayName={'Check Out Time'}
						timeZoneName={'checkOutTimeZone'}
						dateName={'checkOutTime'}
						initialValue={formData.checkOutTime}
						rules={[
							{
								type: 'object',
								required: true,
								message: 'Please select the time technician(s) finished work.',
							},
							{
								message: 'Check-out time must be after check-in time.',
								validator: (_, value) => {
									if (!value) return Promise.resolve();

									const checkOutUtcIso = wallTimeToUtcIso(value, checkOutTimeZone);
									const checkOutUtc = moment(checkOutUtcIso).utc();

									const checkInUtc = moment(checkInTime).utc().subtract(2, 'minute');

									if (checkOutUtc.isBefore(checkInUtc)) {
										return Promise.reject(new Error('Check-out time must be after check-in time!'));
									}
									return Promise.resolve();
								},
							},
							{
								message: 'Check-out time must be before the current time.',
								validator: (_, value) => {
									if (!value) return Promise.resolve();

									const checkOutUtcIso = wallTimeToUtcIso(value, checkOutTimeZone);
									const checkOutUtc = moment(checkOutUtcIso).utc();

									const nowUtc = moment().utc().add(15, 'minute');

									if (checkOutUtc.isAfter(nowUtc)) {
										return Promise.reject(
											new Error('Check-out time must be before the current time!')
										);
									}
									return Promise.resolve();
								},
							},
						]}
						disabledDate={getDisabledDate(checkInTime, checkOutTimeZone, moment())}
						disabledTime={getDisabledDateTime(
							moment(checkInTime).subtract(1, 'minute'),
							checkOutTimeZone,
							moment().add(1, 'minute')
						)}
					/>
					<FormItem label="Add After Photos">
						{getFieldDecorator(`images`, {
							valuePropName: 'fileList',
							getValueFromEvent: normFile,
							initialValue: this.state.allPhotos,
							rules: getImageCountErrorRules(numImagesRequired, imageCountErrorMessage),
						})(
							<OWUpload
								accept="image/*"
								name="file"
								listType="picture-card"
								className="servicecall-image-uploader"
								multiple={true}
								headers={uploadHeaders}
								onPreview={this.handlePreview}
								action={`${BACKEND_URI}/api/v1/supplier/file/upload`}
								beforeUpload={imageUploadValidation}
								onChange={this.handleImageUpdate}
								onRemove={this.handleRemove}
							>
								<div style={{ display: 'inline' }}>
									<LegacyIcon type={this.state.uploadLoading ? 'loading' : 'plus'} />
									<div className="ant-upload-text">Upload</div>
								</div>
								<Modal visible={previewVisible} footer={null} onCancel={this.handleCancel}>
									<img alt="example" style={{ width: '100%' }} src={previewImage} />
								</Modal>
							</OWUpload>
						)}
					</FormItem>
					<FormItem label="Notes">
						{getFieldDecorator('checkOutNotes', {
							initialValue: {
								text: formData.checkOutNotes,
								audioUrl: formData.checkOutAudio,
							},
							rules: [
								{
									validator: (_, val, callback) => {
										if (checkOutNotesMandatory && !(val && val.text) && !(val && val.audioUrl)) {
											callback('Please add notes or a voice note');
										} else {
											callback();
										}
									},
								},
							],
						})(
							<TextAreaWithAudio
								style={{ maxWidth: 300 }}
								userType={userType}
								onBusyStateChange={(busy) => this.setState({ isAudioBusy: busy })}
							/>
						)}
					</FormItem>
					{hasResolutionTypes ? (
						<Form.Item label="Resolution Type">
							{getFieldDecorator('resolutionTypeId', {
								initialValue: formData.resolutionTypeId,
							})(
								<OWAsyncSelect
									style={{ maxWidth: 800 }}
									stateSlice={resolutionTypes}
									targetCollectionName={'RESOLUTION_TYPES_AUTOCOMPLETE'}
									fetchMultiple={(ids, targetCollectionName) => {
										fetchMultipleResolutionTypes(ids, targetCollectionName);
									}}
									fetchData={(
										searchText,
										targetCollectionName,
										pagination,
										sorting,
										filters,
										addToTargetCollection
									) => {
										fetchResolutionTypes(
											{ name: searchText },
											targetCollectionName,
											pagination,
											sorting,
											filters,
											addToTargetCollection
										);
									}}
									renderRecord={(resolutionType) => (
										<Select.Option key={resolutionType.id} value={resolutionType.id}>
											{resolutionType.resolutionCode ? `${resolutionType.resolutionCode} - ` : null}
											{resolutionType.resolutionType}
										</Select.Option>
									)}
									sortBy={{ sort_by: 'name', order: 'ascend' }}
									allowClear
								/>
							)}
						</Form.Item>
					) : null}
				</Form>
			</Modal>
		);
	}
}

const mapStateToProps = (state, ownProps) => ({
	visible: ownProps.visible,
	onCancel: ownProps.onCancel,
	onSubmit: ownProps.onSubmit,
	currentUser: state.session.currentUser,
	companyConfig: state.company_config,
	createErrors: state.buyer_contacts.createErrors,
	updateErrors: state.buyer_contacts.updateErrors,
	creating: state.work_orders.creating,
	updating: state.work_orders.updating,

	privateSupplier: ownProps.privateSupplier,
	resolutionTypes: state.resolution_types,
});

const mapDispatchToProps = (dispatch) => ({
	fetchMultipleResolutionTypes: (ids, targetCollectionName) =>
		dispatch(resolutionTypesRestCrudThunksForSupplier.readMultiple(ids, targetCollectionName)),
	fetchResolutionTypes: (
		params,
		targetCollectionName,
		pagination,
		sorting,
		filters,
		addToTargetCollection
	) =>
		dispatch(
			resolutionTypesRestCrudThunksForSupplier.read(
				params,
				targetCollectionName,
				pagination,
				sorting,
				filters,
				addToTargetCollection
			)
		),
	fetchTaskItems: (
		params,
		targetCollectionName,
		pagination,
		sorting,
		filters,
		addToTargetCollection
	) =>
		dispatch(
			taskItemsRestCrudThunksForSupplier.read(
				params,
				targetCollectionName,
				pagination,
				sorting,
				filters,
				addToTargetCollection
			)
		),
});

export default connect(
	mapStateToProps,
	mapDispatchToProps
)(Form.create<ManualCheckOutModalFormProps>()(ManualCheckOutModalForm));
